import { registrationFormValues } from "@/components/events/eventFormValues";
import { useState, useEffect } from "react";
import { Check, X, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormSection } from "@/components/ui/form-section";
import { StatusBadge } from "./StatusBadge";
import { useUpdateEvent, useUpdateEventStatus } from "@/hooks/useEvents";
import { useCategories } from "@/hooks/useCategories";
import { useVenues } from "@/hooks/useVenues";
import { eventDateInput, eventFormDates } from "@/lib/eventDates";
import { toast } from "sonner";
import type { EventResponse } from "@/types";
import { EventImageManager } from "./EventImageManager";
import { EventFormFields } from "./EventFormFields";
import { emptyEventFormValues, type EventFormValues } from "./eventFormValues";

interface EventDialogProps {
  event: EventResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventDialog({ event, open, onOpenChange }: EventDialogProps) {
  const updateEvent = useUpdateEvent();
  const updateStatus = useUpdateEventStatus();
  const { data: categories = [] } = useCategories();
  const { data: venues = [] } = useVenues();

  const [form, setForm] = useState<EventFormValues>(emptyEventFormValues());

  useEffect(() => {
    if (event) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        title: event.title,
        eventDescription: event.eventDescription,
        date: eventDateInput(event.date, event.isAllDay),
        endDate: eventDateInput(event.endDate, event.isAllDay),
        isAllDay: event.isAllDay,
        venueId: event.venueId ?? "",
        categoryId: event.categoryId ?? "",
        detailsUrl: event.detailsUrl,
        ...registrationFormValues(event),
        source: event.source,
        sourceUrl: event.sourceUrl,
        isPreview: event.isPreview,
      });
    }
  }, [event]);

  const handleChange = (patch: Partial<EventFormValues>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    try {
      const dates = eventFormDates(form);
      if (!dates.date) throw new Error("Podaj datę rozpoczęcia.");
      await updateEvent.mutateAsync({
        id: event.id,
        body: {
          title: form.title,
          eventDescription: form.eventDescription,
          date: dates.date,
          endDate: dates.endDate,
          isAllDay: form.isAllDay,
          venueId: form.venueId,
          categoryId: form.categoryId,
          detailsUrl: form.detailsUrl,
          registrationOptions: form.registrationOptions,
          registrationNotes: form.registrationNotes,
          source: form.source,
          sourceUrl: form.sourceUrl,
          isPreview: form.isPreview,
        },
      });
      toast.success("Wydarzenie zaktualizowane");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleQuickStatus = async (status: "approved" | "rejected") => {
    if (!event) return;
    try {
      await updateStatus.mutateAsync({ id: event.id, status });
      toast.success(status === "approved" ? "Wydarzenie zatwierdzone" : "Wydarzenie odrzucone");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zmienić statusu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const isPending = event?.status === "pending";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden bg-pulso-bg p-0">
        <DialogHeader className="border-b border-black/5 bg-pulso-surface px-6 py-4 text-left">
          <DialogTitle className="font-brand text-xl">Edytuj wydarzenie</DialogTitle>
          {event && <p className="text-xs text-pulso-muted">{event.id}</p>}
        </DialogHeader>

        <form id="event-edit-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <EventFormFields
            values={form}
            onChange={handleChange}
            categories={categories}
            venues={venues}
            showPreviewToggle
            afterFields={
              event ? (
                <FormSection icon={ImageIcon} title="Zdjęcia">
                  <EventImageManager eventId={event.id} />
                </FormSection>
              ) : null
            }
          />
        </form>

        <DialogFooter className="flex-row items-center justify-between border-t border-black/5 bg-pulso-surface px-6 py-4">
          <div className="flex items-center gap-2">
            {event && <StatusBadge status={event.status} rejectionReason={event.rejectionReason} />}
            {isPending && (
              <>
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 bg-pulso-success text-white hover:bg-pulso-success/90"
                  onClick={() => handleQuickStatus("approved")}
                  disabled={updateStatus.isPending}
                >
                  <Check className="h-4 w-4" strokeWidth={3} /> Zatwierdź
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  onClick={() => handleQuickStatus("rejected")}
                  disabled={updateStatus.isPending}
                >
                  <X className="h-4 w-4" strokeWidth={3} /> Odrzuć
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Anuluj
            </Button>
            <Button type="submit" form="event-edit-form" disabled={updateEvent.isPending}>
              {updateEvent.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
